import React, { memo } from 'react';
import { View } from 'react-native';

import TimeBlock from './components/timeBlock';
import PTimeBlock from './components/pTimeBlock';
import TimelineTop from './components/timelineTop';
import HorizontalBottomLine from './components/horizontalBottomLine';

import Svg from '@styles/icons/svg';
import ColorWrapper from '@styles/colors';

import { LineChartProps } from './types';
import { scale } from '@components/frame';
import { useManageHistogram } from './hooks/useManageHistogram';

const LineChart: React.FC<LineChartProps> = ({
  C,
  data,
  elementWidth,
  P25 = 0,
  P50 = 0,
  P75 = 0,
  loaded,
  hiddenNullPercentiles = false,
  showPercentiles = true,
  mainColor,
}) => {
  const {
    circleSize,
    topPopUpWidth,
    bottomPopUpWidth,
    formattedPositions,
    topLines,
    P25Position,
    P75Position,
    formattedBottomBlocks,
  } = useManageHistogram({ data, elementWidth, P25, P50, P75, hiddenNullPercentiles });

  return (
    <View style={{ flex: 1, marginBottom: !showPercentiles ? 30 : 0 }}>
      <View style={{ width: elementWidth }}>
        {!loaded ? (
          // Loading state
          <View style={{ width: '100%' }}>
            <View style={{ width: '100%', flexDirection: 'row', height: 66 }} />
            <View style={{ position: 'relative' }}>
              <View
                nativeID="shimmer"
                style={{
                  height: 8,
                  borderRadius: 4,
                  position: 'absolute',
                  left: 40,
                  width: elementWidth - 80,
                  top: -2,
                  overflow: 'hidden',
                  backgroundColor: C('Gray/Light/3'),
                }}
              />
              <View
                style={{
                  width: '100%',
                  height: 4,
                  backgroundColor: C('Gray/Light/3'),
                  borderRadius: 2,
                  opacity: 0.2,
                }}
              />
            </View>
            <View style={{ width: '100%', position: 'relative', height: 100 }} />
          </View>
        ) : (
          <View style={{ width: '100%' }}>
            <View style={{ width: '100%', flexDirection: 'row', height: 66 }}>
              {formattedPositions.map((element) => (
                <TimeBlock
                  key={element.name}
                  C={C}
                  seconds={element.value}
                  color={element.color}
                  name={element.name}
                  left={element.formattedPosition}
                  topPopupWidth={topPopUpWidth}
                  zIndex={element.zIndex}
                />
              ))}
            </View>
            <View style={{ position: 'relative' }}>
              {topLines.map(({ line, element, zIndex }) => (
                <View
                  key={element.name}
                  style={{
                    position: 'absolute',
                    width: line.lineWidth,
                    left: line.lineStart,
                    height: 1,
                    backgroundColor: C(element.color),
                    zIndex,
                    top: -10,
                  }}
                />
              ))}
              {showPercentiles && (
                <View
                  style={{
                    height: 8,
                    backgroundColor: C(mainColor),
                    borderRadius: 4,
                    position: 'absolute',
                    left: P25Position,
                    width: P75Position + 1 - P25Position,
                    top: -2,
                  }}
                />
              )}
              {formattedPositions.map((element) => {
                const left = element.position - circleSize / 2;
                return (
                  <View
                    key={element.name}
                    style={{
                      width: circleSize,
                      height: circleSize,
                      borderRadius: circleSize / 2,
                      borderColor: C('Background/Light/System'),
                      borderWidth: 1,
                      backgroundColor: C('Background/Light/System'),
                      position: 'absolute',
                      zIndex: element.zIndex,
                      top: -5,
                      left,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <View
                      style={{
                        width: 1,
                        height: 6,
                        backgroundColor: C(element.color),
                        position: 'absolute',
                        top: -6,
                        left: 6,
                      }}
                    />
                    <Svg
                      icon={element.icon}
                      color={element.color}
                      {...(element?.width ? { width: scale(element.width) } : {})}
                      {...(element?.height ? { height: scale(element.height) } : {})}
                    />
                  </View>
                );
              })}
              <View
                style={{
                  width: '100%',
                  height: 4,
                  backgroundColor: C(mainColor),
                  borderRadius: 2,
                  opacity: showPercentiles ? 0.2 : 1,
                }}
              />
              {showPercentiles && (
                <View style={{ width: '100%', position: 'relative', height: 75 }}>
                  {formattedBottomBlocks.map((block) => {
                    if (hiddenNullPercentiles && block.seconds === 0) {
                      return null;
                    }

                    return (
                      <React.Fragment key={block.name}>
                        <TimelineTop C={C} left={block.position} averageColor={mainColor} />
                        <HorizontalBottomLine C={C} left={block.lineStart} width={block.lineWidth} averageColor={mainColor} />
                        <PTimeBlock
                          seconds={block.seconds}
                          C={C}
                          name={block.name}
                          left={block.formattedPosition}
                          bottomPopupWidth={bottomPopUpWidth}
                          zIndex={block.zIndex}
                          averageColor={mainColor}
                        />
                      </React.Fragment>
                    );
                  })}
                </View>
              )}
            </View>
          </View>
        )}
      </View>
    </View>
  );
};

export default memo(ColorWrapper(LineChart));
